import * as React from "react";
import socketIOClient from "socket.io-client";
import { useAuthContext, useAuthUser } from "./GameAuthContext";

interface ContextValue {
	socket: SocketIOClient.Socket | undefined;
	connected: boolean;
}
const ctx = React.createContext<ContextValue | undefined>(undefined);

export const SocketProvider: React.FC<{ socketUrl: string }> = ({ socketUrl, children }) => {
	const { logout } = useAuthContext();
	const { token, roomId } = useAuthUser();
	const [socket, setSocket] = React.useState<SocketIOClient.Socket | undefined>(undefined);
	const [connected, setConnected] = React.useState<boolean>(false);

	React.useEffect(() => {
		const socketConnection = socketIOClient(socketUrl, {
			transports: ["websocket"],
			upgrade: true,
			query: {
				roomId,
				token,
			},
		});
		socketConnection.on("error", ({ code }) => {
			if (code === "auth/invalid-token") {
				logout();
			}
		});
		socketConnection.on("connect", () => {
			setConnected(true);
		});
		socketConnection.on("disconnect", () => {
			setConnected(false);
		});
		socketConnection.connect();
		setSocket(socketConnection);
		return () => {
			socketConnection.disconnect();
		};
	}, [logout, roomId, socketUrl, token]);

	const value = React.useMemo(() => ({ socket, connected }), [connected, socket]);

	return <ctx.Provider value={value}>{children}</ctx.Provider>;
};

export function useSocketContext(): ContextValue {
	const context = React.useContext(ctx);
	if (!context) {
		throw new Error("useSocket must be put within Socket provider");
	}
	return context;
}
